/**
 * Route switch. Each surface is a separate tree; nothing is shared but the
 * design tokens and the display primitives in ui/bits.
 */
import { Component, type ReactNode } from 'react';
import { FacultyApp } from '../faculty/FacultyApp.tsx';
import { loadSeat, saveSeat } from '../net/api.ts';
import { PlayerApp, Splash } from '../player/PlayerApp.tsx';
import { ProjectorApp } from '../projector/ProjectorApp.tsx';
import { SpectatorApp } from '../faculty/SpectatorApp.tsx';
import { JoinScreen } from './JoinScreen.tsx';
import { VersionNotice } from './VersionNotice.tsx';
import { navigate, useRoute } from './router.ts';

export function App() {
  const r = useRoute();
  const [first, second] = r.parts;
  let page: ReactNode;
  if (!first || first === 'join') {
    page = <JoinScreen initialCode={r.query.get('code') ?? ''} initialTable={r.query.get('table') ? Number(r.query.get('table')) : null} />;
  } else if (first === 'play') {
    const seat = loadSeat();
    page = seat ? (
      <PlayerApp
        key={seat.token}
        token={seat.token}
        sessionId={seat.sessionId}
        onLeave={() => {
          saveSeat(null);
          navigate(`/join?code=${seat.code}&table=${seat.table}`);
        }}
      />
    ) : (
      <JoinScreen initialCode="" initialTable={null} />
    );
  } else if (first === 'control') {
    page = <FacultyApp route={r} />;
  } else if (first === 'watch' && second) {
    page = <SpectatorApp token={second} />;
  } else if (first === 'projector' && second) {
    page = <ProjectorApp token={second} />;
  } else {
    page = <Splash text="Nothing here. Check the link." />;
  }
  return <Boundary><VersionNotice />{page}</Boundary>;
}

/** Last-resort error boundary: show a way back rather than a white screen. */
class Boundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error) {
    // Logged to the console for the faculty laptop; the server logs its own side.
    console.error('[nightshift] render failed', error);
  }
  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="splash">
        <p className="huddle-line display">Something on this screen broke.</p>
        <p className="muted">Your seat and the game are safe on the server. Reload to carry on.</p>
        <button className="btn btn-primary" onClick={() => location.reload()}>
          Reload
        </button>
      </main>
    );
  }
}
